
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiPlus,
  FiRefreshCw,
  FiEdit2,
  FiUsers,
  FiSearch,
  FiPower,
  FiX,
  FiCalendar,
  FiShield,
  FiUserCheck,
  FiUser,
} from "react-icons/fi";
import api from "../../utills/api";
import BreadCrumb from "../../Components/Common/BreadCrumb";

import {
  Card,
  Col,
  CardHeader,
  Row,
 
} from "reactstrap";

const ROLES = [
  { value: "Supervisor", label: "Supervisor" },
  { value: "Counsellor", label: "Counsellor" },
  { value: "Staff", label: "Team Member" },
  { value: "Admin", label: "Admin" },
];

const EMPTY_FORM = {
  name: "",
  email: "",
  phone: "",
  role: "Counsellor",
  joiningDate: new Date().toLocaleDateString("en-CA"),
  password: "",
};

const getUserId = (user) => user?._id || user?.id;

const getDateInputValue = (value) => {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "";

  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
};

const formatDate = (value) => {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getRoleLabel = (role) =>
  role === "Staff" ? "Team Member" : role || "—";

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [errorMessage, setErrorMessage] = useState("");

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage("");

      const response = await api.get("/organization/users");

      const data =
        response?.data?.data?.users ||
        response?.data?.data ||
        response?.data;

      if (!Array.isArray(data)) {
        throw new Error("Unexpected response while loading team members.");
      }

      setUsers(data);
    } catch (error) {
      console.error("Failed to load team members:", error);
      setUsers([]);
      setErrorMessage(
        error?.response?.data?.message ||
          "Unable to load team members. Please check your access and try again."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const stats = useMemo(() => {
    return {
      total: users.length,
      active: users.filter((user) => user.isActive !== false).length,
      supervisors: users.filter(
        (user) => user.role === "Supervisor"
      ).length,
      counsellors: users.filter(
        (user) => user.role === "Counsellor"
      ).length,
      teamMembers: users.filter(
        (user) => user.role === "Staff"
      ).length,
    };
  }, [users]);

  const filteredUsers = useMemo(() => {
    const query = search.trim().toLowerCase();

    return users.filter((user) => {
      const matchesSearch =
        !query ||
        [user.name, user.email, user.phone, getRoleLabel(user.role)]
          .some((value) =>
            String(value || "").toLowerCase().includes(query)
          );

      const matchesRole =
        roleFilter === "All" || user.role === roleFilter;

      const matchesStatus =
        statusFilter === "All" ||
        (statusFilter === "Active" && user.isActive !== false) ||
        (statusFilter === "Inactive" && user.isActive === false);

      return matchesSearch && matchesRole && matchesStatus;
    });
  }, [users, search, roleFilter, statusFilter]);

  const openCreate = () => {
    setEditingUser(null);
    setForm({
      ...EMPTY_FORM,
      joiningDate: new Date().toLocaleDateString("en-CA"),
    });
    setErrorMessage("");
    setModalOpen(true);
  };

  const openEdit = (user) => {
    setEditingUser(user);
    setForm({
      name: user.name || "",
      email: user.email || "",
      phone: user.phone || "",
      role: user.role || "Counsellor",
      joiningDate: getDateInputValue(user.joiningDate),
      password: "",
    });
    setErrorMessage("");
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;

    setModalOpen(false);
    setEditingUser(null);
    setForm(EMPTY_FORM);
    setErrorMessage("");
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const saveUser = async (event) => {
    event.preventDefault();
    setSaving(true);
    setErrorMessage("");

    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        role: form.role,
        joiningDate: form.joiningDate,
      };

      if (form.password) {
        payload.password = form.password;
      }

      if (editingUser) {
        await api.put(
          `/organization/users/${getUserId(editingUser)}`,
          payload
        );
      } else {
        if (!form.password || form.password.length < 8) {
          setErrorMessage("Password must be at least 8 characters.");
          setSaving(false);
          return;
        }

        await api.post("/organization/users", payload);
      }

      setModalOpen(false);
      setEditingUser(null);
      setForm(EMPTY_FORM);

      await loadUsers();
    } catch (error) {
      console.error("Failed to save team member:", error);
      setErrorMessage(
        error?.response?.data?.message ||
          "Unable to save team member."
      );
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (user) => {
    const id = getUserId(user);
    const nextStatus = user.isActive === false;

    const action = nextStatus ? "activate" : "deactivate";

    if (
      !window.confirm(
        `Are you sure you want to ${action} ${user.name || "this team member"}?`
      )
    ) {
      return;
    }

    try {
      setUpdatingId(id);

      await api.put(`/organization/users/${id}`, {
        isActive: nextStatus,
      });

      await loadUsers();
    } catch (error) {
      console.error("Failed to update team member status:", error);

      window.alert(
        error?.response?.data?.message ||
          `Unable to ${action} team member.`
      );
    } finally {
      setUpdatingId(null);
    }
  };

  const statCards = [
    {
      label: "Total Members",
      value: stats.total,
      icon: FiUsers,
      color: "primary",
    },
    {
      label: "Active Members",
      value: stats.active,
      icon: FiUserCheck,
      color: "success",
    },
    {
      label: "Supervisors",
      value: stats.supervisors,
      icon: FiShield,
      color: "info",
    },
    {
      label: "Counsellors",
      value: stats.counsellors,
      icon: FiUser,
      color: "warning",
    },
  ];
document.title =
    "Users | eduLeads.Online";
  return (
    <div className="page-content">
      <div className="container-fluid">
            <BreadCrumb
          title="Team Management"
          pageTitle="Settings"
        />

          <Row>
                  <Col lg={12}>
                    <Card>
                      <CardHeader>
                        <div className="d-flex align-items-center flex-wrap gap-2">
                          <div className="flex-grow-1">
                            <h5 className="mb-0">
                              Team Management
                            </h5>
        
                            <small className="text-muted">
                               Manage team members, responsibilities and account status.
                            </small>
                          </div>
            <button
              type="button"
              className="btn btn-light"
              onClick={loadUsers}
              disabled={loading}
            >
              <FiRefreshCw size={15} className="me-2" />
              Refresh
            </button>

            <button
              type="button"
              className="btn btn-primary"
              onClick={openCreate}
            >
              <FiPlus size={16} className="me-2" />
              Add Team Member
            </button>
          
                        </div>
                       
                      </CardHeader>
                    </Card>
                  </Col>
                </Row>
        {/* Page header */}
  

        {/* Summary cards */}
        <div className="row g-3 mb-4">
          {statCards.map((item) => {
            const Icon = item.icon;

            return (
              <div className="col-12 col-sm-6 col-xl-3" key={item.label}>
                <div className="card h-100 mb-0">
                  <div className="card-body d-flex justify-content-between align-items-center">
                    <div>
                      <p className="text-muted mb-2">{item.label}</p>
                      <h3 className="mb-0">{item.value}</h3>
                    </div>

                    <div
                      className={`bg-${item.color}-subtle text-${item.color} rounded p-3`}
                    >
                      <Icon size={22} />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Team list */}
        <div className="card">
          <div className="card-header bg-white border-bottom">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
              <div>
                <h5 className="mb-1">Team Members</h5>
                <p className="text-muted small mb-0">
                  {filteredUsers.length} of {users.length} members
                </p>
              </div>

              <div
                className="d-flex flex-wrap gap-2"
                style={{ minWidth: "min(100%, 280px)" }}
              >
                <div className="input-group" style={{ maxWidth: 300 }}>
                  <span className="input-group-text bg-white">
                    <FiSearch />
                  </span>
                  <input
                    type="search"
                    className="form-control"
                    placeholder="Search team..."
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                  />
                </div>

                <select
                  className="form-select"
                  style={{ maxWidth: 170 }}
                  value={roleFilter}
                  onChange={(event) => setRoleFilter(event.target.value)}
                  aria-label="Filter by role"
                >
                  <option value="All">All roles</option>
                  {ROLES.map((role) => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </select>

                <select
                  className="form-select"
                  style={{ maxWidth: 150 }}
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                  aria-label="Filter by status"
                >
                  <option value="All">All statuses</option>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
            </div>
          </div>

          <div className="card-body p-0">
            {errorMessage && !modalOpen && (
              <div className="alert alert-danger m-3" role="alert">
                {errorMessage}
              </div>
            )}

            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border spinner-border-sm text-primary me-2" />
                Loading team members...
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table align-middle table-hover mb-0">
                  <thead className="table-light">
                    <tr>
                      <th className="ps-4">Team Member</th>
                      <th>Role</th>
                      <th>Contact</th>
                      <th>Joining Date</th>
                      <th>Status</th>
                      <th className="text-end pe-4">Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {filteredUsers.length === 0 ? (
                      <tr>
                        <td
                          colSpan="6"
                          className="text-center py-5 text-muted"
                        >
                          <FiUsers size={30} className="mb-3" />
                          <p className="mb-1">No team members found.</p>
                          <small>
                            Try changing your filters or add a team member.
                          </small>
                        </td>
                      </tr>
                    ) : (
                      filteredUsers.map((user) => {
                        const id = getUserId(user);
                        const inactive = user.isActive === false;

                        return (
                          <tr key={id}>
                            <td className="ps-4">
                              <div className="d-flex align-items-center gap-3">
                                <div
                                  className="rounded-circle bg-light d-flex align-items-center justify-content-center flex-shrink-0"
                                  style={{
                                    width: 42,
                                    height: 42,
                                  }}
                                >
                                  <FiUser size={19} />
                                </div>

                                <div>
                                  <div className="fw-semibold">
                                    {user.name || "—"}
                                  </div>
                                  <small className="text-muted">
                                    {user.email || "—"}
                                  </small>
                                </div>
                              </div>
                            </td>

                            <td>
                              <span className="badge bg-light text-dark border">
                                {getRoleLabel(user.role)}
                              </span>
                            </td>

                            <td>{user.phone || "—"}</td>

                            <td>
                              <span className="text-nowrap">
                                <FiCalendar
                                  size={14}
                                  className="text-muted me-2"
                                />
                                {formatDate(user.joiningDate)}
                              </span>
                            </td>

                            <td>
                              <span
                                className={`badge ${
                                  inactive
                                    ? "bg-danger-subtle text-danger"
                                    : "bg-success-subtle text-success"
                                }`}
                              >
                                {inactive ? "Inactive" : "Active"}
                              </span>
                            </td>

                            <td className="text-end pe-4">
                              <button
                                type="button"
                                className="btn btn-sm btn-light me-1"
                                title="Edit team member"
                                onClick={() => openEdit(user)}
                              >
                                <FiEdit2 size={14} />
                              </button>

                              <button
                                type="button"
                                className={`btn btn-sm ${
                                  inactive
                                    ? "btn-outline-success"
                                    : "btn-outline-danger"
                                }`}
                                title={inactive ? "Activate" : "Deactivate"}
                                disabled={updatingId === id}
                                onClick={() => toggleStatus(user)}
                              >
                                {updatingId === id ? (
                                  <span className="spinner-border spinner-border-sm" />
                                ) : (
                                  <FiPower size={14} />
                                )}
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Performance integration status */}
        <div className="card mt-4">
          <div className="card-body d-flex align-items-start gap-3">
            <div className="rounded bg-light p-3">
              <FiUserCheck size={22} />
            </div>
            <div>
              <h5 className="mb-1">Team Performance</h5>
              <p className="text-muted mb-0">
                Team membership and account status are available now.
                Assigned leads, completed and pending tasks, and admission
                conversions will be connected after the task and automatic
                assignment modules are implemented.
              </p>
            </div>
          </div>
        </div>

        {/* Create / edit modal */}
        {modalOpen && (
          <div
            className="modal d-block"
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
            style={{ backgroundColor: "rgba(0,0,0,.45)" }}
          >
            <div className="modal-dialog modal-dialog-centered modal-lg">
              <div className="modal-content">
                <form onSubmit={saveUser}>
                  <div className="modal-header">
                    <div>
                      <h5 className="modal-title">
                        {editingUser ? "Edit Team Member" : "Add Team Member"}
                      </h5>
                      <small className="text-muted">
                        Enter the member's account and role details.
                      </small>
                    </div>

                    <button
                      type="button"
                      className="btn btn-light"
                      onClick={closeModal}
                      disabled={saving}
                      aria-label="Close"
                    >
                      <FiX />
                    </button>
                  </div>

                  <div className="modal-body">
                    {errorMessage && (
                      <div className="alert alert-danger" role="alert">
                        {errorMessage}
                      </div>
                    )}

                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="form-label">Full Name *</label>
                        <input
                          name="name"
                          className="form-control"
                          value={form.name}
                          onChange={handleChange}
                          required
                          maxLength={120}
                          autoComplete="name"
                        />
                      </div>

                      <div className="col-md-6">
                        <label className="form-label">Email *</label>
                        <input
                          type="email"
                          name="email"
                          className="form-control"
                          value={form.email}
                          onChange={handleChange}
                          required
                          autoComplete="email"
                        />
                      </div>

                      <div className="col-md-6">
                        <label className="form-label">Mobile Number</label>
                        <input
                          type="tel"
                          name="phone"
                          className="form-control"
                          value={form.phone}
                          onChange={handleChange}
                          autoComplete="tel"
                        />
                      </div>

                      <div className="col-md-6">
                        <label className="form-label">Role *</label>
                        <select
                          name="role"
                          className="form-select"
                          value={form.role}
                          onChange={handleChange}
                          required
                        >
                          {ROLES.map((role) => (
                            <option key={role.value} value={role.value}>
                              {role.label}
                            </option>
                          ))}
                        </select>
                        <small className="text-muted">
                          Team Member is stored as the existing Staff role.
                        </small>
                      </div>

                      <div className="col-md-6">
                        <label className="form-label">Joining Date *</label>
                        <input
                          type="date"
                          name="joiningDate"
                          className="form-control"
                          value={form.joiningDate}
                          onChange={handleChange}
                          required
                        />
                      </div>

                      <div className="col-md-6">
                        <label className="form-label">
                          {editingUser ? "New Password (optional)" : "Password *"}
                        </label>
                        <input
                          type="password"
                          name="password"
                          className="form-control"
                          value={form.password}
                          onChange={handleChange}
                          required={!editingUser}
                          minLength={8}
                          autoComplete="new-password"
                          placeholder={
                            editingUser
                              ? "Leave blank to keep current password"
                              : "Minimum 8 characters"
                          }
                        />
                      </div>
                    </div>
                  </div>

                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-light"
                      onClick={closeModal}
                      disabled={saving}
                    >
                      Cancel
                    </button>

                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={saving}
                    >
                      {saving ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" />
                          Saving...
                        </>
                      ) : editingUser ? (
                        "Save Changes"
                      ) : (
                        "Create Team Member"
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Users;
